import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { safeBRL, safeNumber } from "@/lib/formatters";
import MiniKpi from "./MiniKpi";

/** "Painel financeiro da empresa" — monthly BVSM result panel with previous-month delta.
 * Only renders for admin/admin_master/coordenacao/diretoria roles. */
export default function BvsmResultCard({ isAdmin }) {
  const { user } = useAuth();
  const allowed = ["admin", "admin_master", "coordenacao", "diretoria"].includes(user?.role);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previous, setPrevious] = useState(null);

  function startEnd(ym) {
    const [y, m] = ym.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
  }

  function prevMonth(ym) {
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    return d.toISOString().slice(0, 7);
  }

  useEffect(() => {
    if (!allowed) return;
    const { from, to } = startEnd(month);
    setLoading(true);
    api.get("/bvsm/control", { params: { date_from: from, date_to: to } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    const prev = startEnd(prevMonth(month));
    api.get("/bvsm/control", { params: { date_from: prev.from, date_to: prev.to } })
      .then((r) => setPrevious(r.data?.totals?.resultado_bvsm ?? null))
      .catch(() => setPrevious(null));
  }, [month, allowed]);

  if (!allowed) return null;

  const t = data?.totals || {};
  const resultado = Number(t.resultado_bvsm || 0);
  const margemPct = Number(t.resultado_bvsm_pct || 0);
  const delta = previous != null ? resultado - previous : null;
  const deltaPct = previous != null && previous !== 0
    ? ((resultado - previous) / Math.abs(previous)) * 100
    : 0;

  function adjustMonth(deltaMonths) {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + deltaMonths, 1);
    setMonth(d.toISOString().slice(0, 7));
  }

  return (
    <Card className="border-slate-300 shadow-md bg-gradient-to-br from-slate-50 via-emerald-50/30 to-blue-50/40" data-testid="bvsm-result-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Resultado BVSM (líquido após custos e impostos)</div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Painel financeiro da empresa</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => adjustMonth(-1)} data-testid="btn-prev-bvsm-month" className="h-9">◀</Button>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="h-11 md:h-9 min-h-[44px] px-3 rounded-md border border-slate-300 text-base md:text-sm bg-white" data-testid="input-bvsm-month" />
            <Button variant="outline" size="sm" onClick={() => adjustMonth(1)} data-testid="btn-next-bvsm-month" className="h-9">▶</Button>
            {isAdmin && (
              <Link to="/controle-bvsm" className="ml-2">
                <Button variant="ghost" size="sm" className="h-9 text-blue-700">Detalhes →</Button>
              </Link>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <div className="md:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className={`text-4xl md:text-5xl font-display font-bold ${resultado >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="bvsm-result-value">
                {loading ? "—" : safeBRL(resultado)}
              </div>
              <div className={`text-lg font-semibold px-3 py-1 rounded-full ${resultado >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                {margemPct.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%
              </div>
            </div>
            {delta != null && (
              <div className="mt-2 text-sm">
                <span className="text-slate-500">vs. mês anterior:</span>{" "}
                <span className={`font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="bvsm-delta">
                  {delta >= 0 ? "+" : ""}{safeBRL(delta)} ({deltaPct >= 0 ? "+" : ""}{deltaPct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%)
                </span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <MiniKpi label="Faturado" value={safeBRL(t.total_faturado)} tone="slate" />
            <MiniKpi label="Repasse cirurg." value={safeBRL(t.repasse_cirurgioes)} tone="amber" />
            <MiniKpi label="Imposto BVSM" value={safeBRL(t.imposto_bvsm)} tone="rose" />
            <MiniKpi label="Apex" value={safeBRL(t.comissao_apex)} tone="violet" />
            <MiniKpi label="Instrument." value={safeBRL(t.custo_instrumentador)} tone="cyan" />
            <MiniKpi label="Casos" value={safeNumber(t.cases)} tone="blue" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
